import { LICENSE_MODULE } from "@selector/shared";
import { values } from "ramda";
import { Component, createRef } from "react";
import { connect } from "react-redux";
import { type Field, Form, type FormErrors, formValueSelector, type InjectedFormProps, reduxForm } from "redux-form";
import { HStack, InfoToggletip } from "widgets";

import {
  getFormError,
  getFormWarning,
  getSelectedOption,
  handleTabInForm,
  type ISelectOption,
  simpleDropdownFieldValidation,
  simpleDropdownMultiSelectFieldValidation,
  simpleFieldValidation,
} from "@/app-utils/form.ts";
import { getUiContextOptions } from "@/app-utils/playbook.ts";
import {
  type Configuration,
  type NfWorkflow,
  type PlaybookCategory,
  type SampleType,
  UiContextEnum,
  type Workflow,
  type WorkflowP2,
  type WorkflowParam,
} from "@/graphql.ts";
import { isClassicProcessorDisabled, isLicensed } from "@/redux/modules/config.ts";
import { type IListingState, loadApprovedWorkflows } from "@/redux/modules/listing.ts";
import {
  checkExecContextCompatibleWithP2Params,
  fetchNfWorkflows,
  fetchPublishedWorkflows,
  type IPlaybookConfigState,
  loadAdminPlaybookCategories,
  updatePlaybookConfigState,
  validatePlaybookExists,
} from "@/redux/modules/playbook-admin.ts";
import { isNgsDataFileWorkflowParam } from "@/redux/modules/playbook-execution.ts";
import { type ISampleAdminState, loadSampleTypes } from "@/redux/modules/sample-admin.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { type SelectorIcon, SelectorIcons } from "@/settings/assets-settings.ts";
import { PLAYBOOK_TYPE, PLAYBOOK_TYPE_LABELS } from "@/settings/settings.ts";
import { WorkflowSelectorReduxFormField } from "@/ui-modules/core/redux-form/WorkflowSelectorFormField.tsx";

import TabFence from "../../core/forms/TabFence.js";
import { ProtectedResourceLogo } from "../../core/ProtectedResourceLogo.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type IPlaybookConfigBasicsInfoScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface IPlaybookConfigBasicsInfoScreenAppStateProps {
  globalFormError: string;
  globalFormWarning: string;
  listing: IListingState;
  sampleAdmin: ISampleAdminState;
  initialPlaybookFormState: Partial<IPlaybookConfigState>; // needed to know if we are in edit mode e.g. for async name validation
  playbookTypeOptions: ISelectOption[];
  initialValues: Partial<FormData>;
  selectedPlaybookType: ISelectOption;
  selectedExecutionContexts: ISelectOption[];
  playbookCategories: PlaybookCategory[];
  configuration: Configuration;
  originalWorkflowId: string | number | undefined;
}

interface IPlaybookConfigBasicsInfoScreenDispatchProps {
  updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => void;
  loadSampleTypes: () => Promise<SampleType[]>;
  loadApprovedWorkflows: (playbookTypeId: number) => Promise<Workflow[]>;
  loadNfWorkflows: () => Promise<NfWorkflow[]>;
  loadPublishedWorkflows: () => Promise<WorkflowP2[]>;
  loadAdminPlaybookCategories: () => Promise<PlaybookCategory[]>;
  validatePlaybookExists: (name: string, suffix: string) => Promise<boolean>;
}

export interface IPlaybookConfigBasicsInfoScreenProps
  extends IPlaybookConfigBasicsInfoScreenAppStateProps,
    IPlaybookConfigBasicsInfoScreenDispatchProps,
    IPlaybookConfigBasicsInfoScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IPlaybookConfigBasicsInfoScreenOwnProps,
): IPlaybookConfigBasicsInfoScreenAppStateProps {
  const playbookFormState: Partial<IPlaybookConfigState> = state.playbookAdminState.playbookConfigState;
  const playbookTypeOptions: ISelectOption[] = PlaybookConfigBasicsInfoScreenImpl.getPlaybookTypeOptions(
    state.config.configuration,
  );

  return {
    globalFormError: getFormError(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    globalFormWarning: getFormWarning(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME, state),
    listing: state.listing,
    sampleAdmin: state.sampleAdmin,
    initialPlaybookFormState: state.playbookAdminState.playbookConfigState,
    playbookTypeOptions: playbookTypeOptions,
    playbookCategories: state.playbookAdminState.adminPlaybookCategories,
    configuration: state.config.configuration,
    originalWorkflowId: playbookFormState?.workflowId,
    initialValues: {
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NAME]: playbookFormState?.name,
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SUFFIX]: playbookFormState?.nameSuffix,
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_DESCRIPTION]: playbookFormState?.description,
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_ICON]:
        playbookFormState?.icon || SelectorIcons.SAMPLE2,
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE]: playbookFormState.playbookType
        ? getSelectedOption(playbookTypeOptions, playbookFormState.playbookType)
        : playbookTypeOptions[0],
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_CATEGORY]:
        playbookFormState?.playbookCategories,
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT]: playbookFormState?.uiContexts
        ? playbookFormState?.uiContexts.map((c) => getSelectedOption(getUiContextOptions(), c))
        : [],
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE]: getSelectedOption<SampleType>(
        state.sampleAdmin.sampleTypes,
        playbookFormState?.sampleTypeId,
        (sampleType) => sampleType.id,
      ),
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW]: getSelectedOption<Workflow>(
        state.listing.approvedWorkflows,
        playbookFormState?.workflowId,
        (workflow) => workflow.sourceId,
      ),
      [PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW]: playbookFormState?.workflowId,
    },
    selectedPlaybookType: formValueSelector(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME)(
      state,
      PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE,
    ),
    selectedExecutionContexts: formValueSelector(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME)(
      state,
      PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT,
    ),
  };
}

function mapDispatchToProps(dispatch): IPlaybookConfigBasicsInfoScreenDispatchProps {
  return {
    updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => dispatch(updatePlaybookConfigState(state)),
    loadSampleTypes: () => dispatch(loadSampleTypes()),
    loadApprovedWorkflows: (playbookTypeId: number) => dispatch(loadApprovedWorkflows(null, playbookTypeId)),
    loadNfWorkflows: () => dispatch(fetchNfWorkflows()),
    loadPublishedWorkflows: () => dispatch(fetchPublishedWorkflows()),
    loadAdminPlaybookCategories: () => dispatch(loadAdminPlaybookCategories({ throwError: true, showAlert: false })),
    validatePlaybookExists: (name: string, suffix: string) => dispatch(validatePlaybookExists(name, suffix)),
  };
}

interface IPlaybookConfigBasicsInfoScreenState {
  nfWorkflows: NfWorkflow[];
  isLoadingNfWorkflows: boolean;
}

class PlaybookConfigBasicsInfoScreenImpl extends Component<
  IPlaybookConfigBasicsInfoScreenProps,
  IPlaybookConfigBasicsInfoScreenState
> {
  public static PLAYBOOK_CREATION_FORM_NAME = "PLAYBOOK_CREATION_BASICS_INFO";
  // field identifiers
  public static PLAYBOOK_CREATION_FIELD_NAME = "PLAYBOOK_CREATION_FIELD_NAME";
  public static PLAYBOOK_CREATION_FIELD_SUFFIX = "PLAYBOOK_CREATION_FIELD_SUFFIX";
  public static PLAYBOOK_CREATION_FIELD_DESCRIPTION = "PLAYBOOK_CREATION_FIELD_DESCRIPTION";
  public static PLAYBOOK_CREATION_FIELD_ICON = "PLAYBOOK_CREATION_FIELD_ICON";
  public static PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE = "PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE";
  public static PLAYBOOK_CREATION_FIELD_PLAYBOOK_CATEGORY = "PLAYBOOK_CREATION_FIELD_PLAYBOOK_CATEGORY";
  public static PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT = "PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT";
  public static PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE = "PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE";
  public static PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW = "PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW";
  public static PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW = "PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW";
  public static PLAYBOOK_CREATION_FIELD_P2_WORKFLOW = "PLAYBOOK_CREATION_FIELD_P2_WORKFLOW";

  public static PLAYBOOK_ICONS: SelectorIcon[] = values(SelectorIcons);

  private lastTabRef = createRef<Field>();
  private firstTabRef = createRef<Field>();

  state = {
    nfWorkflows: [],
    isLoadingNfWorkflows: false,
  };

  constructor(props) {
    super(props);
  }

  public static getPlaybookTypeOptions = (configuration: Configuration): ISelectOption[] => {
    const options: ISelectOption[] = [];
    if (isLicensed(configuration, LICENSE_MODULE.PROCESSOR)) {
      options.push(
        {
          value: PLAYBOOK_TYPE.PROCESSOR,
          label: PLAYBOOK_TYPE_LABELS.PROCESSOR,
        },
        {
          value: PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS,
          label: PLAYBOOK_TYPE_LABELS.NEXTFLOW_ANALYSIS,
        },
      );

      if (isLicensed(configuration, LICENSE_MODULE.TRACKER)) {
        options.push({
          value: PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES,
          label: PLAYBOOK_TYPE_LABELS.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES,
        });
      }
    }
    if (isLicensed(configuration, LICENSE_MODULE.PROCESSOR) && !isClassicProcessorDisabled(configuration)) {
      options.push({
        value: PLAYBOOK_TYPE.RANGE_ANALYSIS,
        label: PLAYBOOK_TYPE_LABELS.RANGE_ANALYSIS,
      });
    }
    if (isLicensed(configuration, LICENSE_MODULE.PROCESSOR) && !isClassicProcessorDisabled(configuration)) {
      options.push({
        value: PLAYBOOK_TYPE.NGS_ANALYSIS,
        label: PLAYBOOK_TYPE_LABELS.NGS_ANALYSIS,
      });
      if (isLicensed(configuration, LICENSE_MODULE.TRACKER)) {
        options.push({
          value: PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES,
          label: PLAYBOOK_TYPE_LABELS.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES,
        });
      }
    }
    if (isLicensed(configuration, LICENSE_MODULE.TRACKER)) {
      options.push({
        value: PLAYBOOK_TYPE.SAMPLE_REGISTRATION,
        label: PLAYBOOK_TYPE_LABELS.SAMPLE_REGISTRATION,
      });
    }
    options.push(
      {
        value: PLAYBOOK_TYPE.REPORT_FROM_VARIANTS,
        label: PLAYBOOK_TYPE_LABELS.REPORT_FROM_VARIANTS,
      },
      {
        value: PLAYBOOK_TYPE.REPORT_FROM_COORDINATES,
        label: PLAYBOOK_TYPE_LABELS.REPORT_FROM_COORDINATES,
      },
    );
    return options;
  };

  componentDidMount() {
    if (isLicensed(this.props.configuration, LICENSE_MODULE.TRACKER)) {
      this.props
        .loadSampleTypes()
        .then((sampleTypes) => {
          const sampleTypeOption = getSelectedOption<SampleType>(
            sampleTypes,
            this.props.initialPlaybookFormState?.sampleTypeId,
            (sampleType) => sampleType.id,
          );
          this.props.change(PlaybookConfigBasicsInfoScreen.PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE, sampleTypeOption);
        })
        .catch((error: Error) => this.props.updateWizardMessage(error.message));
    }
    // in the case the configured type is not available any loner (wrt licensing or capabilities) we indicate it to the admin
    const selectedPlaybookType = this.props.initialPlaybookFormState.playbookType;
    if (selectedPlaybookType && !getSelectedOption(this.props.playbookTypeOptions, selectedPlaybookType)) {
      this.props.updateWizardMessage(
        `This Playbook uses a software module that is no longer available to you. Please check your user role assignment and software license.`,
      );
    }

    // fetch the listing of Nextflow workflows and set the initial in case of editing
    if (isLicensed(this.props.configuration, LICENSE_MODULE.PROCESSOR)) {
      this.setState({ isLoadingNfWorkflows: true });
      this.props
        .loadNfWorkflows()
        .then((nfWorkflows) => {
          this.setState({ nfWorkflows: nfWorkflows, isLoadingNfWorkflows: false });
          const nfWorkflowOption = getSelectedOption<NfWorkflow>(
            nfWorkflows,
            this.props.initialPlaybookFormState?.workflowId,
            (workflow) => workflow.name,
          );
          this.props.change(PlaybookConfigBasicsInfoScreen.PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW, nfWorkflowOption);
        })
        .catch((error: Error) => {
          this.setState({ isLoadingNfWorkflows: false });
          console.error(error);
          this.props.updateWizardMessage(error.message);
        });
    }

    this.props.loadAdminPlaybookCategories().catch((error: Error) => this.props.updateWizardMessage(error.message));
  }

  componentDidUpdate(prevProps: Readonly<IPlaybookConfigBasicsInfoScreenProps>) {
    if (
      this.props.selectedPlaybookType !== prevProps.selectedPlaybookType &&
      prevProps.selectedPlaybookType != undefined
    ) {
      // reset workflowId selection because some ids are numbers, others are string. @see handleSubmitData
      this.props.change(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW, null);
      this.props.change(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW, null);
      this.props.change(PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW, null);
    }

    // loading the approved workflows only happens on update because at mount time the playbook type is not set
    // (because of asynchronous initialization of redux)
    if (
      this.props.selectedPlaybookType !== prevProps.selectedPlaybookType &&
      needsClassicProcessorWorkflow(this.props.selectedPlaybookType?.value)
    ) {
      this.props
        .loadApprovedWorkflows(this.props.selectedPlaybookType.value)
        .then((approvedWorkflows) => {
          const approvedWorkflowOption = getSelectedOption<Workflow>(
            approvedWorkflows,
            this.props.initialPlaybookFormState?.workflowId,
            (workflow) => workflow.sourceId,
          );
          this.props.change(
            PlaybookConfigBasicsInfoScreen.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW,
            approvedWorkflowOption,
          );
        })
        .catch((error: Error) => {
          console.error(error);
          this.props.updateWizardMessage(error.message);
        });
    }
  }

  handleSubmitData = (values) => {
    // at this stage the validation has succeeded

    let workflowId = null;
    if (needsClassicProcessorWorkflow(this.props.selectedPlaybookType.value)) {
      workflowId = values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW]?.sourceId;
    } else if (needsNextFlowWorkflow(this.props.selectedPlaybookType.value)) {
      workflowId = values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW]?.name;
    } else if (needsP2Workflow(this.props.selectedPlaybookType.value)) {
      workflowId = values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW];
    }

    const playbookCreationState: Partial<IPlaybookConfigState> = {
      name: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NAME].trim(),
      nameSuffix: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SUFFIX]?.trim(),
      description: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_DESCRIPTION],
      icon: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_ICON],
      sampleTypeId: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE]?.id,
      playbookType: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE].value,
      playbookCategories: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_CATEGORY],
      uiContexts: values[PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT]?.map(
        (c: ISelectOption) => c.value,
      ),
      workflowId: workflowId,
    };

    // This resets the p2 parameters when the workflow selection was changed in this screen.
    // Although this could also be done in the redux module, it seems better here
    // because it is ensured that the logic is only executed for the first screen.
    if (workflowId !== this.props.originalWorkflowId) {
      playbookCreationState.p2Params = undefined;
    }

    this.props.updatePlaybookCreationState(playbookCreationState);

    return this.props.proceedWithNextStep();
  };

  iconOptionRenderer = (icon: SelectorIcon) => {
    return <img src={icon.large} alt="" />;
  };

  render() {
    const isLastScreen = false; // currently ths screen is never the last screen
    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: isLastScreen ? "Save Playbook" : "Next",
            isLoading: this.props.submitting || !!this.props.asyncValidating,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form className="form-horizontal" onSubmit={this.props.handleSubmit(this.handleSubmitData)}>
          <div onKeyDownCapture={handleTabInForm}>
            <TabFence targetRef={this.lastTabRef} />

            <InputReduxFormField
              key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NAME}
              name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NAME}
              horizontalLayout={true}
              label="Name"
              component={InputField}
              placeholder="Playbook name"
              maxLength={50}
              disabled={false}
              tooltip="Give your Playbook a name"
              validate={simpleFieldValidation}
              inputRef={this.firstTabRef}
            />

            <InputReduxFormField
              key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SUFFIX}
              name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SUFFIX}
              horizontalLayout={true}
              label={
                <HStack>
                  Name suffix
                  <InfoToggletip content="Optionally provide a suffix to distinguish Playbooks with the same name. If your Playbook has a unique name, no suffix is required." />
                </HStack>
              }
              component={InputField}
              placeholder="Optional suffix"
              maxLength={150}
              disabled={false}
              inputRef={this.firstTabRef}
            />

            <InputReduxFormField
              key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_DESCRIPTION}
              name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_DESCRIPTION}
              horizontalLayout={true}
              label="Description"
              component={InputField}
              placeholder="Playbook description"
              maxLength={500}
              disabled={false}
              tooltip="Give your Playbook a description"
              inputRef={this.lastTabRef}
            />

            <ReactSelectReduxFormField
              key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_ICON}
              name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_ICON}
              horizontalLayout={true}
              isClearable={false}
              isSearchable={false}
              label="Playbook icon"
              component={ReactSelectField}
              formatOptionLabel={this.iconOptionRenderer}
              options={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_ICONS}
              getOptionValue={(option: SelectorIcon) => option.id}
              getOptionLabel={(option: SelectorIcon) => option.large}
              isDisabled={false}
              customStyles={{
                valueContainer: (provided) => ({
                  ...provided,
                  height: "100px",
                }),
                container: (provided) => ({
                  ...provided,
                  width: "200px",
                }),
              }}
            />

            <ReactSelectReduxFormField
              key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE}
              name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE}
              formGroupClassName="input-field-playbook-type"
              horizontalLayout={true}
              isClearable={false}
              isSearchable={false}
              label="Playbook type"
              component={ReactSelectField}
              options={this.props.playbookTypeOptions}
              isDisabled={false}
              info={this.getPlaybookDescription()}
            />

            {this.props.selectedPlaybookType?.value !== PLAYBOOK_TYPE.BLAST && (
              <ReactSelectReduxFormField
                key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_CATEGORY}
                name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PLAYBOOK_CATEGORY}
                formGroupClassName="input-field-playbook-category"
                horizontalLayout={true}
                isClearable={false}
                isSearchable={true}
                label={
                  <HStack>
                    Select a category
                    <InfoToggletip content="The Playbook will be displayed next to related playbooks that belong to a common category. Users are able to browse the categories to locate this playbook." />
                  </HStack>
                }
                placeholder="Select a category"
                component={ReactSelectField}
                options={this.props.playbookCategories.filter(
                  (category: PlaybookCategory) => category.name.toLowerCase() !== "blast",
                )}
                getOptionLabel={(category: PlaybookCategory) => category.name}
                getOptionValue={(category: PlaybookCategory) => category.id}
                isDisabled={false}
                isMulti={true}
                validate={simpleDropdownMultiSelectFieldValidation}
              />
            )}

            {needsP2Workflow(this.props.selectedPlaybookType?.value) && (
              <ReactSelectReduxFormField
                key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT}
                name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT}
                formGroupClassName="input-field-playbook-context"
                horizontalLayout={true}
                isClearable={false}
                isSearchable={true}
                label={
                  <HStack>
                    Select an execution context
                    <InfoToggletip content="The Playbook will be available for execution in the selected contexts." />
                  </HStack>
                }
                placeholder="Select a context"
                component={ReactSelectField}
                options={getUiContextOptions()}
                getOptionLabel={(context) => context.label}
                isDisabled={false}
                isMulti={true}
                validate={simpleDropdownMultiSelectFieldValidation}
              />
            )}

            {needsSampleType(this.props.selectedPlaybookType?.value) && (
              <ReactSelectReduxFormField
                key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE}
                name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SAMPLE_TYPE}
                horizontalLayout={true}
                isClearable={true}
                isSearchable={true}
                getOptionValue={(option: SampleType) => option.id}
                getOptionLabel={(option: SampleType) => option.name}
                label={
                  <HStack>
                    Sample type
                    <InfoToggletip
                      content={`A sample type must be assigned to this Playbook based on the Playbook type selected above.
                      The sample type defines the content permissions and attributes that are available for each of the samples that are used with this playbook.
                      If an appropriate sample type does not yet exist, you must exit this dialog and create the sample type before you can proceed.`}
                    />
                  </HStack>
                }
                component={ReactSelectField}
                isDisabled={false}
                isLoading={this.props.sampleAdmin.isLoadingSampleTypes}
                options={this.props.sampleAdmin.sampleTypes}
                validate={simpleDropdownFieldValidation}
              />
            )}

            {needsClassicProcessorWorkflow(this.props.selectedPlaybookType?.value) && (
              <ReactSelectReduxFormField
                key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW}
                name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW}
                label={
                  <HStack>
                    Select a workflow
                    <InfoToggletip
                      content={`This is the data processing workflow that will be run when a user submits the
                      playbook. You can only select a workflow that has been approved inside Classic Processor and is
                      compatible with the Playbook type selected above. If an appropriate workflow is not available
                      here, you must create and approve the workflow in Classic Processor first before you can proceed.`}
                    />
                  </HStack>
                }
                horizontalLayout={true}
                isClearable={true}
                isSearchable={true}
                getOptionValue={(option: Workflow) => option.sourceId}
                getOptionLabel={(option: Workflow) => option.name}
                formatOptionLabel={(option: Workflow) => (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>{option.name}</div>
                    {option.isDemo && <ProtectedResourceLogo />}
                  </div>
                )}
                component={ReactSelectField}
                isDisabled={false}
                isLoading={this.props.listing.isLoadingApprovedWorkflows}
                options={this.props.listing.approvedWorkflows}
                validate={simpleDropdownFieldValidation}
              />
            )}

            {needsNextFlowWorkflow(this.props.selectedPlaybookType?.value) && (
              <ReactSelectReduxFormField
                key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW}
                name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NEXTFLOW_WORKFLOW}
                label={
                  <HStack>
                    Select a workflow
                    <InfoToggletip
                      content={`This is the data processing workflow that will be run when a user submits the
                      playbook. You can only select a workflow that is
                      compatible with the Playbook type selected above. If an appropriate workflow is not available
                      here, you must create a Nextflow Pipeline first before you can proceed.`}
                    />
                  </HStack>
                }
                horizontalLayout={true}
                isClearable={true}
                isSearchable={true}
                getOptionValue={(option: NfWorkflow) => option.name}
                getOptionLabel={(option: NfWorkflow) => option.name}
                component={ReactSelectField}
                isDisabled={false}
                isLoading={this.state.isLoadingNfWorkflows}
                options={this.state.nfWorkflows}
                validate={simpleDropdownFieldValidation}
              />
            )}

            {needsP2Workflow(this.props.selectedPlaybookType?.value) && (
              <WorkflowSelectorReduxFormField
                key={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW}
                name={PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW}
                title={
                  <HStack>
                    Select a workflow
                    <InfoToggletip
                      content={`This is the data processing workflow that will be run when a user submits the
                      playbook. You can only select a workflow that is
                      compatible with the Playbook type selected above. If an appropriate workflow is not available
                      here, you must create and publish the workflow in Processor first before you can proceed.`}
                    />
                  </HStack>
                }
                horizontalLayout={true}
                validate={simpleFieldValidation}
                // workflowId={1337}
                // handleChange={(workflowId) => console.log("selected: ", workflowId)}
              />
            )}

            <TabFence targetRef={this.firstTabRef} />
          </div>
        </Form>
      </PlaybookWizardStep>
    );
  }

  getPlaybookDescription = () => {
    switch (this.props.selectedPlaybookType?.value) {
      case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES: {
        return (
          <>
            <i>Playbook will be displayed on the Playbook launch page and on sample tables</i>
            <br />
            <br />
            User provides inputs that are defined by an approved Classic Processor workflow. Results are saved to
            Tracker and a Selector project.
            <br />
            <br />
            The Playbook can associate results with samples that are registered in Tracker.
          </>
        );
      }
      case PLAYBOOK_TYPE.SAMPLE_REGISTRATION: {
        return (
          <>
            <i>Playbook will be displayed on the Playbook launch page</i>
            <br />
            <br />
            User provides information to register new samples in Tracker.
          </>
        );
      }
      case PLAYBOOK_TYPE.NGS_ANALYSIS: {
        return (
          <>
            <i>Playbook will be displayed on the Playbook launch page</i>
            <br />
            <br />
            User provides inputs that are defined by an approved Classic Processor workflow. Results are saved to a
            Selector project.
          </>
        );
      }
      case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS: {
        return (
          <>
            <i>Playbook will be displayed on the Playbook launch page</i>
            <br />
            <br />
            User provides inputs that are defined by an imported Nextflow pipeline. Results are saved to a Selector
            project.
          </>
        );
      }
      case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES: {
        return (
          <>
            <i>Playbook will be displayed on the Playbook launch page and on sample tables</i>
            <br />
            <br />
            User provides inputs that are defined by an imported Nextflow pipeline. Results are saved to Tracker and a
            Selector project.
            <br />
            <br />
            The Playbook can associate results with samples that are registered in Tracker.
          </>
        );
      }
      case PLAYBOOK_TYPE.RANGE_ANALYSIS: {
        return (
          <>
            <i>Playbook will be displayed on the Playbook launch page and in the Genome Viewer</i>
            <br />
            <br />
            User provides a reference genome, a genomic range, and additional inputs as defined by an approved Classic
            Processor workflow. Results are saved to a Selector project.
          </>
        );
      }
      case PLAYBOOK_TYPE.REPORT_FROM_VARIANTS: {
        return (
          <>
            <i>Playbook will be displayed on variant tables</i>
            <br />
            <br />
            User provides a selection of variants, genomes, and other inputs that are defined by a Nextflow workflow. A
            custom report is saved to a Selector project.
          </>
        );
      }
      case PLAYBOOK_TYPE.REPORT_FROM_COORDINATES: {
        return (
          <>
            <i>Playbook will be displayed on coordinate list tables</i>
            <br />
            <br />
            User provides a Coordinate List, a selection of genomes, and other inputs that are defined by a Nextflow
            workflow. A custom report is saved to a Selector project.
          </>
        );
      }
      case PLAYBOOK_TYPE.PROCESSOR: {
        return (
          <>
            User provides inputs, as defined by a published Processor workflow.
            <br />
            Results are saved to a Selector project.
          </>
        );
      }
    }
  };

  static async asyncValidate(values, dispatch, props: IPlaybookConfigBasicsInfoScreenProps) {
    const CLASS = PlaybookConfigBasicsInfoScreenImpl;

    if (!values) {
      return;
    }

    const selectedWorkflowId: number = values[CLASS.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW];
    const selectedExecutionContexts = values[CLASS.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT].map(
      (c: ISelectOption) => c.value,
    );

    if (
      // since the validation takes some time, restrict it strictly to the required scenarios
      values[CLASS.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE]?.value === PLAYBOOK_TYPE.PROCESSOR &&
      selectedWorkflowId &&
      selectedExecutionContexts?.length > 0 &&
      // do not validate if the Playbook is just configured for the Playbooks page since there are no dependencies
      !(selectedExecutionContexts?.length === 1 && selectedExecutionContexts[0] === UiContextEnum.PlaybookPage)
    ) {
      const isCompatible = await dispatch(
        checkExecContextCompatibleWithP2Params(selectedExecutionContexts, selectedWorkflowId),
      );

      if (!isCompatible) {
        throw {
          _error:
            "This workflow is missing required staged parameters for the selected execution contexts. Please ensure all required parameters are staged in the workflow.",
        };
      }
    }

    // empty playbook name should be handled by the field level validation
    const playbookName = values[CLASS.PLAYBOOK_CREATION_FIELD_NAME]?.trim();
    const nameSuffix = values[CLASS.PLAYBOOK_CREATION_FIELD_SUFFIX]?.trim();

    // editing case
    // beware: the nameSuffix will be initialized by redux form as empty string (not null) when the field is touched
    const normalizedNameSuffix: string = nameSuffix || null; // turn empty string to null
    if (
      !playbookName ||
      (props.initialPlaybookFormState?.initialPlaybook?.name === playbookName &&
        props.initialPlaybookFormState?.initialPlaybook?.nameSuffix == normalizedNameSuffix)
    ) {
      return;
    }

    let playbookNameAlreadyExists: boolean;
    try {
      playbookNameAlreadyExists = await props.validatePlaybookExists(playbookName, nameSuffix);
    } catch (error: Error) {
      throw { _error: error.message };
    }

    if (playbookNameAlreadyExists) {
      throw {
        [CLASS.PLAYBOOK_CREATION_FIELD_NAME]: nameSuffix
          ? "This combination of playbook name and suffix already exists"
          : "This playbook name already exists. Either change it or provide a name suffix to create a new playbook group member.",
        [CLASS.PLAYBOOK_CREATION_FIELD_SUFFIX]: nameSuffix
          ? "This combination of playbook name and suffix already exists"
          : `Enter a suffix to create a new member of playbook group "${playbookName}"`,
      };
    }
  }

  static validate(values: FormData): FormErrors<any, FormData> {
    const CLASS = PlaybookConfigBasicsInfoScreenImpl;

    if (!values) {
      return {};
    }

    const playbookType: ISelectOption = values[CLASS.PLAYBOOK_CREATION_FIELD_PLAYBOOK_TYPE];
    const selectedWorkflow: Workflow = values[CLASS.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW];

    // make sure the expected workflow input parameter is available in the selected approved workflow
    if (needsClassicProcessorWorkflow(playbookType?.value) && selectedWorkflow) {
      if (
        playbookType?.value === PLAYBOOK_TYPE.NGS_ANALYSIS ||
        playbookType?.value === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES
      ) {
        const ngsRawDataFilePathWorkflowParam: WorkflowParam =
          selectedWorkflow.workflowParams.find(isNgsDataFileWorkflowParam);

        if (!ngsRawDataFilePathWorkflowParam) {
          return {
            [CLASS.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW]: (
              <>
                In order to be compatible with NGS Analysis, the Approved Workflow needs to have as input a parameter
                for NGS Data files.
              </>
            ),
          };
        }
      }
      if (playbookType?.value === PLAYBOOK_TYPE.RANGE_ANALYSIS) {
        // check something
      }

      if (!selectedWorkflow.isValid) {
        return {
          [CLASS.PLAYBOOK_CREATION_FIELD_PROCESSOR_WORKFLOW]: (
            <>This workflow seems to be invalid. Please check within Processor or choose another workflow.</>
          ),
        };
      }
    }
    return {};
  }
}

const PlaybookConfigBasicsInfoScreen = connect<
  IPlaybookConfigBasicsInfoScreenAppStateProps,
  IPlaybookConfigBasicsInfoScreenDispatchProps,
  IPlaybookConfigBasicsInfoScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FORM_NAME,
    asyncValidate: PlaybookConfigBasicsInfoScreenImpl.asyncValidate,
    asyncBlurFields: [
      PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_NAME,
      PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_SUFFIX,
      PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_EXECUTION_CONTEXT,
      PlaybookConfigBasicsInfoScreenImpl.PLAYBOOK_CREATION_FIELD_P2_WORKFLOW,
    ],
    validate: PlaybookConfigBasicsInfoScreenImpl.validate, // for global validation
    // destroyOnUnmount: false
  })(PlaybookConfigBasicsInfoScreenImpl as any) as any,
);

const needsNextFlowWorkflow = (playbookType: PLAYBOOK_TYPE) => {
  return (
    playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS ||
    playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES ||
    playbookType === PLAYBOOK_TYPE.REPORT_FROM_COORDINATES ||
    playbookType === PLAYBOOK_TYPE.REPORT_FROM_VARIANTS
  );
};

const needsP2Workflow = (playbookType: PLAYBOOK_TYPE) => {
  return playbookType === PLAYBOOK_TYPE.PROCESSOR;
};

const needsClassicProcessorWorkflow = (playbookType: PLAYBOOK_TYPE) => {
  return (
    playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS ||
    playbookType === PLAYBOOK_TYPE.RANGE_ANALYSIS ||
    playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES
  );
};

const needsSampleType = (playbookType: PLAYBOOK_TYPE) => {
  return (
    playbookType === PLAYBOOK_TYPE.SAMPLE_REGISTRATION ||
    playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES ||
    playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES
  );
};

export { PlaybookConfigBasicsInfoScreen, PlaybookConfigBasicsInfoScreenImpl };
